/**
 * Payload de carrossel (Evolution API `sendCarousel`) para a Proposta Digital.
 * Estrutura idêntica ao modelo fornecido: { body, cards: [{ body, footer, imageUrl, buttons }] }.
 * Módulo puro: usado pela Edge Function e pelos testes Vitest.
 */

export interface QuoteCarouselItemInput {
  name: string;
  price: number;
  imageUrl?: string | null;
  /** Link individual do item; quando ausente usa o link da proposta. */
  url?: string | null;
}

export interface CarouselButton {
  type: 'url';
  displayText: string;
  url: string;
}

export interface CarouselCard {
  body: string;
  footer: string;
  imageUrl: string;
  buttons: CarouselButton[];
}

export interface QuoteCarouselPayload {
  body: string;
  cards: CarouselCard[];
}

/** WhatsApp limita carrosséis a 10 cards. */
export const MAX_CAROUSEL_CARDS = 10;

/**
 * Texto de abertura do carrossel — fixo, só interpola o nome do cliente.
 * O link da proposta NÃO entra aqui: ele vai nos botões "Ver proposta" de cada card.
 */
export const QUOTE_CAROUSEL_INTRO_TEMPLATE = [
  'Olá, {nome}! É um prazer dar continuidade ao planejamento do seu evento.',
  '',
  'Preparei as propostas de cardápio com base na variedade e qualidade que conversamos.',
  '',
  '📲 Confira as opções no carrossel abaixo:',
  '',
  'Clique em "Ver proposta" na opção desejada para visualizar os detalhes do cardápio.',
  '',
  'Pelo próprio link da proposta, você pode solicitar ajustes, aprovar e realizar a reserva.',
  '',
  'Qualquer dúvida, estou à disposição por aqui!',
].join('\n');

export function buildQuoteCarouselMessage(clientName?: string | null): string {
  const safeName = (clientName ?? '').trim() || 'Cliente';
  return QUOTE_CAROUSEL_INTRO_TEMPLATE.replace(/\{nome\}/gi, safeName);
}

export function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    return new URL(value.trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

export function formatBRL(value: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  return `R$ ${safe.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Monta o payload. Itens sem imagem https são descartados (o WhatsApp exige
 * mídia em todo card). Retorna null quando não sobra nenhum card válido.
 */
export function buildQuoteCarouselPayload(
  message: string,
  proposalUrl: string,
  items: QuoteCarouselItemInput[],
): QuoteCarouselPayload | null {
  if (!isHttpsUrl(proposalUrl)) return null;
  const cards: CarouselCard[] = items
    .filter((item) => item && item.name?.trim() && isHttpsUrl(item.imageUrl))
    .slice(0, MAX_CAROUSEL_CARDS)
    .map((item) => ({
      body: item.name.trim(),
      footer: formatBRL(Number(item.price)),
      imageUrl: (item.imageUrl as string).trim(),
      buttons: [{
        type: 'url',
        displayText: 'Ver proposta',
        url: isHttpsUrl(item.url) ? item.url.trim() : proposalUrl,
      }],
    }));
  if (cards.length === 0) return null;
  // A mensagem principal (body) é o texto de abertura antes do carrossel; o
  // link da proposta vai nos botões "Ver proposta" de cada card.
  return { body: message.trim(), cards };
}
